import { useState, useMemo } from 'react';
import {
  FiCheck,
  FiExternalLink,
  FiFileText,
  FiPackage,
  FiChevronDown,
  FiChevronUp,
  FiArrowDown,
} from 'react-icons/fi';
import {
  RiMoneyDollarCircleLine,
  RiShieldCheckLine,
} from 'react-icons/ri';

import { PrimaryButton } from '../../../../../../components/PrimaryButton';
import { Table, Column } from '../../../../../../components/Table';
import { IBusinessProposalDTO } from '../../index';
import {
  Container,
  SectionBlock,
  SectionHeader,
  SectionTitleWrapper,
  SectionTitleRow,
  SectionTitle,
  SectionSubtitle,
  StatementGrid,
  StatementCard,
  TabBadge,
  ProposalDocumentButton,
  KitGrid,
  KitCard,
  KitSectionCard,
  KitJumpButton,
  ShowMoreButton,
  PlanTableWrapper,
  DocumentsSection,
  DocumentButton,
  FooterCard,
  TermsCheckboxLabel,
} from "./styles";

interface IStepZeroProps {
  proposal: IBusinessProposalDTO | null;
  nextStep: () => void;
  loading: boolean;
}

interface IPriceRow {
  id: string;
  name: string;
  price: number;
  suggestedPrice: number;
}

const defaultKitItems = [
  { qty: 1, name: "Impressora 3D Elegoo Neptune 4 Max", is_gift: false },
  { qty: 6, name: "Filamento PLA Fix it", is_gift: false },
  { qty: 1, name: "Caixa Secadora De Filamentos (Dry Box)", is_gift: true },
  { qty: 1, name: "Soprador Térmico Fix it 3D", is_gift: false },
  { qty: 1, name: "Tesoura multiuso", is_gift: false },
  { qty: 1, name: "Alicate", is_gift: false },
  { qty: 1, name: "Espátula", is_gift: false },
  { qty: 1, name: "Rebarbador", is_gift: false },
  { qty: 1, name: "Pinça", is_gift: false },
  { qty: 1, name: "Escova de latão", is_gift: false },
  { qty: 1, name: "Pincel de limpeza", is_gift: false },
  { qty: 1, name: "Rolo de 5 m e 150 fechos pré-cortados do tipo Velcro", is_gift: false },
  { qty: 2, name: "Rolos de malha tubular 15m", is_gift: false },
  { qty: 1, name: "Fita métrica", is_gift: false },
];

const defaultPrices: IPriceRow[] = [
  { id: "1", name: "Órtese de Punho Curta (FixWrist)", price: 120, suggestedPrice: 350 },
  { id: "2", name: "Órtese de Dedo (FixFinger)", price: 35, suggestedPrice: 120 },
  { id: "3", name: "Órtese de Polegar (FixThumb)", price: 75, suggestedPrice: 220 },
  { id: "4", name: "Órtese de Cotovelo (FixElbow)", price: 180, suggestedPrice: 450 },
  { id: "5", name: "Órtese de Tornozelo (FixAnkle)", price: 220, suggestedPrice: 550 },
  { id: "6", name: "Calha Noturna (FixNight)", price: 260, suggestedPrice: 650 },
];

export function StepZero({ proposal, nextStep, loading }: IStepZeroProps) {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showAllKit, setShowAllKit] = useState(false);

  const formatCurrency = (val?: number | string) => {
    const num = Number(val);
    if (isNaN(num)) return "R$ 16.900,00";
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
  };

  // Itens do Kit Físico (prioriza da proposta metadata.kit_items ordenados por prioridade, senão usa default)
  const kitItems = useMemo(() => {
    const rawItems = proposal?.metadata?.kit_items;
    if (Array.isArray(rawItems) && rawItems.length > 0) {
      const sorted = [...rawItems].sort(
        (a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0)
      );
      return sorted.map((item) => ({
        qty: Number(item.qty) || 1,
        name: item.name,
        is_gift: !!item.is_gift,
      }));
    }
    return defaultKitItems;
  }, [proposal]);

  const totalKitItemsCount = useMemo(() => {
    return kitItems.reduce((acc, item) => acc + (Number(item.qty) || 1), 0);
  }, [kitItems]);

  const displayedKitItems = showAllKit ? kitItems : kitItems.slice(0, 6);

  // Preços dinâmicos da tabela de preço
  const pricesData = useMemo<IPriceRow[]>(() => {
    const tablePrices = proposal?.plan?.price_table?.prices;
    if (Array.isArray(tablePrices) && tablePrices.length > 0) {
      return tablePrices.map((p: any) => ({
        id: p.id || String(Math.random()),
        name: p.solution?.name || p.name || "Órtese Fix it",
        price: Number(p.file_price || p.price || p.value || 0),
        suggestedPrice: Number(p.suggested_sale_price || p.suggested_price || p.solution?.suggested_price || 0),
      }));
    }
    return defaultPrices;
  }, [proposal]);

  // Colunas oficiais do Table
  const priceColumns: Column<IPriceRow>[] = [
    {
      key: "name",
      header: "Órtese Fix it 3D",
      render: (row) => <strong>{row.name}</strong>,
    },
    {
      key: "price",
      header: "Arquivo",
      render: (row) => (
        <span style={{ color: "var(--fixit)", fontWeight: 700, fontSize: "14px" }}>
          {formatCurrency(row.price)}
        </span>
      ),
    },
    {
      key: "suggestedPrice",
      header: "Preço Sugerido",
      render: (row) => (
        <span style={{ color: "var(--dark-gray)", fontWeight: 600, fontSize: "14px" }}>
          {row.suggestedPrice > 0 ? formatCurrency(row.suggestedPrice) : "-"}
        </span>
      ),
    },
  ];

  const baseValue = Number(proposal?.total || proposal?.total_paid || 16900);
  const discountValue = Number(proposal?.discount || 0);
  const finalValue = discountValue > 0 ? Math.max(0, baseValue - discountValue) : baseValue;
  const fixcoinsValue = proposal?.fixcoins_to_add ?? 2000;
  const paymentMethod = proposal?.payment_method || "À Vista (Pix/Boleto)";
  const planPrice = Number(proposal?.plan?.price || 0);
  const isPlanFree = Boolean(proposal?.plan?.is_free) || planPrice === 0;

  return (
    <Container>
      {/* LINHA 1: INVESTIMENTO DE IMPLANTAÇÃO & BENEFÍCIOS E ENTREGAS INCLUSAS (50% / 50%) */}
      <StatementGrid>
        {/* CAIXA 1: INVESTIMENTO */}
        <StatementCard>
          <div className="statement-header">
            <span className="statement-title">
              <RiMoneyDollarCircleLine size={18} color="var(--fixit)" />
              Investimento
            </span>
            {proposal?.proposal_document_url && (
              <ProposalDocumentButton
                href={
                  proposal.proposal_document_url.startsWith("http")
                    ? proposal.proposal_document_url
                    : `${process.env.REACT_APP_STORAGE_BASE_URL || ""}/${proposal.proposal_document_url}`
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                <FiFileText size={13} />
                <span>PDF da Proposta</span>
                <FiExternalLink size={11} />
              </ProposalDocumentButton>
            )}
          </div>

          <div className="statement-rows">
            <div className="statement-row">
              <span className="statement-label">Setup e Implantação</span>
              <span className="statement-val">{formatCurrency(baseValue)}</span>
            </div>

            {discountValue > 0 && (
              <div className="statement-row discount">
                <span className="statement-label">Desconto Comercial</span>
                <span className="statement-val">- {formatCurrency(discountValue)}</span>
              </div>
            )}

            <div className="statement-divider" />

            <div className="statement-total">
              <span className="total-label">Total a Pagar</span>
              <span className="total-val">{formatCurrency(finalValue)}</span>
            </div>

            <div className="statement-divider" />

            <div className="statement-row">
              <span className="statement-label">Crédito Inicial em FixCoins</span>
              <TabBadge>{Number(fixcoinsValue).toLocaleString("pt-BR")} FC</TabBadge>
            </div>

            <div className="statement-row">
              <span className="statement-label">Mensalidade da Plataforma</span>
              {isPlanFree ? (
                <TabBadge $variant="success">Isento</TabBadge>
              ) : (
                <span className="statement-val" style={{ color: "var(--fixit)" }}>
                  {formatCurrency(planPrice)}/mês
                </span>
              )}
            </div>
          </div>

          <div className="statement-footer">
            <div className="footer-item">
              <span>Condição de Pagamento:</span>
              <strong>{paymentMethod}</strong>
            </div>
          </div>
        </StatementCard>

        {/* CAIXA 2: INCLUI */}
        <StatementCard>
          <div className="statement-header">
            <span className="statement-title">
              <RiShieldCheckLine size={18} color="var(--fixit)" />
              Inclui
            </span>
          </div>

          <div className="statement-rows">
            <div className="statement-row">
              <span className="statement-label">Treinamento: Impressão 3D</span>
              <span className="statement-val" style={{ fontWeight: 500, fontSize: "13px", color: "var(--dark-gray)" }}>
                3h (Online/Videoconferência)
              </span>
            </div>

            <div className="statement-row">
              <span className="statement-label">Treinamento: Órteses Fix it 3D</span>
              <span className="statement-val" style={{ fontWeight: 500, fontSize: "13px", color: "var(--dark-gray)" }}>
                6h (Online/Videoconferência)
              </span>
            </div>

            <div className="statement-row">
              <span className="statement-label">Kit de Ferramentas e Insumos</span>
              <KitJumpButton
                type="button"
                onClick={() => {
                  document.getElementById('kit-ferramentas')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                {totalKitItemsCount} {totalKitItemsCount === 1 ? "item incluso" : "itens inclusos"} <FiArrowDown />
              </KitJumpButton>
            </div>

            <div className="statement-row">
              <span className="statement-label">Licença de Uso da Marca</span>
              <TabBadge $variant="success">Homologada</TabBadge>
            </div>

            <div className="statement-row">
              <span className="statement-label">Suporte (Horário Comercial)</span>
              <TabBadge $variant="success">Incluso</TabBadge>
            </div>
          </div>
        </StatementCard>
      </StatementGrid>

      {/* LINHA 2: KIT DE FERRAMENTAS E INSUMOS (BLOCO DEDICADO 100% HORIZONTAL) */}
      <KitSectionCard id="kit-ferramentas">
        <div className="kit-section-header">
          <div className="kit-section-title-wrap">
            <span className="kit-section-title">
              <FiPackage size={18} color="var(--fixit)" />
              Kit de Ferramentas e Insumos
            </span>
            <span className="kit-section-subtitle">
              Equipamentos, insumos e acessórios essenciais inclusos no investimento de implantação.
            </span>
          </div>
        </div>

        <KitGrid>
          {displayedKitItems.map((item, idx) => (
            <KitCard key={idx}>
              <div className="kit-left">
                <span className="quantity-badge">{item.qty}x</span>
                <span className="kit-name" title={item.name}>
                  {item.name}
                </span>
              </div>
              {item.is_gift && (
                <TabBadge $variant="success">Brinde</TabBadge>
              )}
            </KitCard>
          ))}
        </KitGrid>

        {kitItems.length > 6 && (
          <ShowMoreButton type="button" onClick={() => setShowAllKit((prev) => !prev)}>
            {showAllKit ? (
              <>
                <FiChevronUp /> Ver menos itens do kit
              </>
            ) : (
              <>
                <FiChevronDown /> Ver todos os itens do kit
              </>
            )}
          </ShowMoreButton>
        )}
      </KitSectionCard>

      {/* BLOCO 4: TABELA COMERCIAL ÓRTESES FIX IT 3D */}
      <SectionBlock>
        <SectionHeader>
          <SectionTitleWrapper>
            <SectionTitleRow>
              <RiMoneyDollarCircleLine size={20} color="var(--fixit)" />
              <SectionTitle>Tabela Comercial Órteses Fix it 3D</SectionTitle>
            </SectionTitleRow>
          </SectionTitleWrapper>
        </SectionHeader>

        <PlanTableWrapper>
          <Table
            columns={priceColumns}
            data={pricesData}
          />
        </PlanTableWrapper>
      </SectionBlock>

      {/* BLOCO 5: DOCUMENTAÇÃO LEGAL & TERMOS */}
      <SectionBlock>
        <SectionHeader>
          <SectionTitleWrapper>
            <SectionTitleRow>
              <FiFileText size={20} color="var(--fixit)" />
              <SectionTitle>Documentação Legal & Termos</SectionTitle>
            </SectionTitleRow>
            <SectionSubtitle>
              Consulte as diretrizes e termos regulatórios antes de formalizar seu credenciamento.
            </SectionSubtitle>
          </SectionTitleWrapper>
        </SectionHeader>

        <DocumentsSection>
          <DocumentButton href="https://usefixit.com.br/termos-de-uso" target="_blank" rel="noopener noreferrer">
            <FiFileText /> Termos de Uso
          </DocumentButton>
          <DocumentButton href="https://usefixit.com.br/politica-de-privacidade" target="_blank" rel="noopener noreferrer">
            <FiFileText /> Política de Privacidade
          </DocumentButton>
        </DocumentsSection>
      </SectionBlock>

      {/* BLOCO 6: ACEITE DA PROPOSTA E CONTINUAR */}
      <FooterCard>
        <TermsCheckboxLabel>
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
          />
          <div>
            Declaro que li, compreendo e concordo com todos os valores da proposta comercial descritos acima, bem como os{" "}
            <a href="https://usefixit.com.br/termos-de-uso" target="_blank" rel="noopener noreferrer">
              Termos de Uso
            </a>{" "}
            e a{" "}
            <a href="https://usefixit.com.br/politica-de-privacidade" target="_blank" rel="noopener noreferrer">
              Política de Privacidade
            </a>{" "}
            da Fix it.
          </div>
        </TermsCheckboxLabel>

        <PrimaryButton
          type="button"
          text="Aceitar Proposta"
          onClick={nextStep}
          loading={loading}
          disabled={loading || !termsAccepted}
          style={{ minWidth: "180px" }}
        />
      </FooterCard>
    </Container>
  );
}
